// frontend/src/pages/ArticlePage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import ArticleContent from "../components/article/ArticleContent";
import LoadingSpinner from "../components/common/LoadingSpinner";

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [slug, language]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch by slug first, then by ID if slug is numeric
      let response;
      if (!isNaN(slug)) {
        // If slug is numeric, fetch by ID
        response = await apiService.getArticle(slug, language);
      } else {
        // Otherwise fetch by slug
        response = await apiService.getArticleBySlug(slug, language);
      }

      if (response.success) {
        setArticle(response.data);
      } else {
        setError("Article not found");
      }
    } catch (err) {
      console.error("Error fetching article:", err);
      if (err.response?.status === 403) {
        setError("You need a higher contract level to access this article.");
      } else if (err.response?.status === 404) {
        setError("Article not found.");
      } else {
        setError("Failed to load article. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/articles"
            className="inline-flex items-center text-[#00A3E0] hover:text-[#002855] mb-4"
          >
            ← Back to Articles
          </Link>

          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate("/articles")}
              className="bg-[#00A3E0] text-white px-4 py-2 rounded-md hover:bg-[#002855]"
            >
              Go to Articles
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Article not found.</p>
          <button
            onClick={() => navigate("/articles")}
            className="mt-4 bg-[#00A3E0] text-white px-4 py-2 rounded-md hover:bg-[#002855]"
          >
            Go to Articles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link
          to="/articles"
          className="inline-flex items-center text-[#00A3E0] hover:text-[#002855] mb-6"
        >
          ← Back to Articles
        </Link>

        {/* Article Content */}
        <ArticleContent article={article} />

        {/* Article metadata */}
        {article.activation && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Published: {new Date(article.activation).toLocaleDateString('de-DE', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlePage;